import axios from "axios";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const apiUrlPlatos = process.env.REACT_APP_API_PLATOS;
const apiUrlTipos = `${(process.env.REACT_APP_API_PLATOS || 'http://localhost:8085/api/platos/').replace(/\/+$/, '')}/../tipos-plato/activos`;

const CrearPlatos = () => {
    const [nombre, setNombre] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [idTipoPlato, setIdTipoPlato] = useState('');
    const [tiposDisponibles, setTiposDisponibles] = useState([]);
    const [loadingTipos, setLoadingTipos] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        getTipos();
    }, []);

    const getTipos = async () => {
        try {
            const response = await axios.get(apiUrlTipos);
            setTiposDisponibles(response.data);
        } catch (error) {
            console.error('Error al cargar tipos de plato:', error);
            alert('Error al cargar tipos de plato. Se usarán los valores por defecto.');
            setTiposDisponibles([
                { id_tipo: 1, nombre: 'General' },
                { id_tipo: 2, nombre: 'Veggie' },
                { id_tipo: 3, nombre: 'Pechuga' }
            ]);
        } finally {
            setLoadingTipos(false);
        }
    };

    const store = async (e) => {
        e.preventDefault();

        setIsSubmitting(true);
        try {
            await axios.post(apiUrlPlatos, {
                nombre,
                descripcion,
                id_tipo_plato: idTipoPlato || null
            });
            alert('✅ Plato creado exitosamente');
            navigate('/admin/AbmPlatos');
        } catch (error) {
            console.error('Error al crear el plato:', error);
            alert('❌ Error al crear el plato: ' + (error.response?.data?.error || error.message));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        navigate('/admin/AbmPlatos');
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-100 py-8 px-4">
            <div className="max-w-3xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-t-2xl shadow-xl p-6 border-b-4 border-orange-500">
                    <div className="flex items-center gap-4">
                        <div className="bg-gradient-to-br from-orange-500 to-amber-600 p-4 rounded-xl shadow-lg">
                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800">Nuevo Plato</h1>
                            <p className="text-gray-600 mt-1">Crea un nuevo plato para el menú</p>
                        </div>
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={store} className="bg-white rounded-b-2xl shadow-xl p-8">
                    <div className="space-y-6">
                        {/* Tipo de Plato */}
                        <div>
                            <label htmlFor="idTipoPlato" className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                                <span className="text-purple-600">🏷️</span>
                                Tipo de Plato
                                <span className="text-red-500">*</span>
                            </label>
                            <select
                                id="idTipoPlato"
                                value={idTipoPlato}
                                onChange={(e) => setIdTipoPlato(e.target.value)}
                                required
                                disabled={loadingTipos}
                                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all outline-none text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <option value="">
                                    {loadingTipos ? 'Cargando tipos...' : 'Seleccione tipo'}
                                </option>
                                {tiposDisponibles.map((tipoPlato) => (
                                    <option key={tipoPlato.id_tipo} value={tipoPlato.id_tipo}>
                                        {tipoPlato.nombre}
                                    </option>
                                ))}
                            </select>
                            {tiposDisponibles.length === 0 && !loadingTipos && (
                                <p className="mt-2 text-sm text-amber-600">
                                    No hay tipos de plato activos.
                                    <button
                                        type="button"
                                        onClick={() => navigate('/admin/tipos-plato')}
                                        className="ml-1 underline hover:text-amber-700 font-semibold"
                                    >
                                        Crear tipos aquí
                                    </button>
                                </p>
                            )}
                        </div>

                        {/* Nombre */}
                        <div>
                            <label htmlFor="nombre" className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                                <span className="text-orange-600">🍽️</span>
                                Nombre del Plato
                                <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="nombre"
                                placeholder="Ej: Milanesa con puré"
                                value={nombre}
                                onChange={(e) => setNombre(e.target.value)}
                                required
                                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all outline-none text-gray-800 placeholder-gray-400"
                            />
                        </div>

                        {/* Descripción */}
                        <div>
                            <label htmlFor="descripcion" className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                                <span className="text-amber-600">📝</span>
                                Descripción
                                <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                id="descripcion"
                                placeholder="Describe los ingredientes y características del plato"
                                value={descripcion}
                                onChange={(e) => setDescripcion(e.target.value)}
                                required
                                rows={3}
                                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all outline-none text-gray-800 placeholder-gray-400 resize-none"
                            />
                        </div>
                    </div>

                    {/* Botones */}
                    <div className="flex flex-col sm:flex-row gap-4 mt-8 pt-6 border-t-2 border-gray-200">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex-1 bg-gradient-to-r from-orange-600 to-amber-600 text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:from-orange-700 hover:to-amber-700 transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                        >
                            {isSubmitting ? (
                                <span className="flex items-center justify-center gap-2">
                                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Guardando...
                                </span>
                            ) : (
                                <span className="flex items-center justify-center gap-2">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Crear Plato
                                </span>
                            )}
                        </button>

                        <button
                            type="button"
                            onClick={handleCancel}
                            disabled={isSubmitting}
                            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-3 px-6 rounded-lg shadow-md transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="flex items-center justify-center gap-2">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Cancelar
                            </span>
                        </button>
                    </div>
                </form>

                {/* Info adicional */}
                <div className="mt-6 bg-orange-50 border-l-4 border-orange-500 p-4 rounded-r-lg shadow-md">
                    <div className="flex items-start gap-3">
                        <svg className="w-6 h-6 text-orange-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <div>
                            <p className="text-sm font-semibold text-orange-900">Platos globales</p>
                            <p className="text-sm text-orange-700 mt-1">
                                Los platos se crean globalmente y luego se asignan a sedes específicas en el tablero de menú semanal.
                                <button
                                    type="button"
                                    onClick={() => navigate('/admin/tipos-plato')}
                                    className="ml-1 underline hover:text-orange-800 font-semibold"
                                >
                                    Gestionar tipos
                                </button>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CrearPlatos;

